import { NorwegianId } from "norwegian-national-id-validator";
import { FormStateType } from "./formTypes";
import { FromThirdStepData } from "./interfaces";
import { CreateBookingResponseDto } from "@apiModels/CreateBookingResponseDto.csharp";
import { MinMaxAge } from "@apiModels/MinMaxAge.csharp";
import { format } from "date-fns";
import { AppointmentOptionDto } from "../../../../Model/Blocks/Waitlist/AppointmentOptionDto.csharp";
import {bool} from "yup";

const dateAndTimeWithoutOffsetFormat = "yyyy-MM-dd'T'HH:mm:ss";

export const isUnderage = (personalNumber: string, age = 16): boolean => {
  const ssn = NorwegianId(personalNumber);
  return ssn.isValid() && (ssn.age() || 0) < age;
};

export const isOfAge = (personalNumber: string, age = 16): boolean => {
  const ssn = NorwegianId(personalNumber);
  return ssn.isValid() && (ssn.age() || 150) > age;
};

export const minAgeNotBlocking = (
  personalNumber: string | undefined,
  minMaxAge: MinMaxAge | undefined
) => {
  if (minMaxAge?.minAge) {
    if (minMaxAge.showWarningOnly) {
      return true;
    }
    if (!!personalNumber && !minMaxAge.showPreparationRequirementsOnly) {
      return !isUnderage(personalNumber, minMaxAge.minAge);
    }
  }

  return true;
};

export const maxAgeNotBlocking = (
  personalNumber: string | undefined,
  minMaxAge: MinMaxAge | undefined
) => {
  if (minMaxAge?.maxAge) {
    if (minMaxAge.showWarningOnly) return true;
    if (!!personalNumber && !minMaxAge.showPreparationRequirementsOnly) {
      return isUnderage(personalNumber, minMaxAge.maxAge + 1);
    }
  }

  return true;
};

export function thirdStepDataCrateParser(
  data: FromThirdStepData,
  state: FormStateType,
  requireParentPersonalIdNumber: boolean,
  remoteOption?: AppointmentOptionDto
) {
  const {
    subscribeToNewsletter,
    notes,
    terms,
    consent,
    remoteContact,
    ...personalInformation
  } = data;

  return {
    appointment: {
      ...state.appointment,
      startTime: format(
        state.appointment.startTime,
        dateAndTimeWithoutOffsetFormat
      ),
      endTime: format(
        state.appointment.endTime,
        dateAndTimeWithoutOffsetFormat
      ),
    },
    remoteAppointmentOption: remoteOption,
    personalInformation: {
      ...personalInformation,
      city: "Oslo",
      name: data.name ?? "",
      firstName: data.firstName ?? "",
      lastName: data.lastName ?? "",
      streetAddress: "NA",
      postalCode: "0010",
      personalIdNumber: personalInformation.personalIdNumber.toString(),
      parentPersonalIdNumber: requireParentPersonalIdNumber
        ? personalInformation.parentPersonalIdNumber
        : null,
      mobileNumber: personalInformation.mobileNumber.toString(),
    },
    notes,
    applyForMembership: subscribeToNewsletter,
    subscribeToNewsletter,
    waitlistContentId: "",
  };
}

export function thirdStepDataConfirmParser(
  data: FromThirdStepData,
  state: FormStateType,
  requireParentPersonalIdNumber: boolean,
  remoteOption?: AppointmentOptionDto,
  code?: number
) {
  return {
    bookingSessionId: state.bookingSessionId,
    booking: thirdStepDataCrateParser(
      data,
      state,
      requireParentPersonalIdNumber,
      remoteOption
    ),
    confirmationCode: code?.toString(),
  };
}

export function thirdStepDataMetodikaDropInParser(
    data: FromThirdStepData,
    contentId: number,
    requireParentPersonalIdNumber: boolean,
) {
    const {
        subscribeToNewsletter,
        notes,
        terms,
        consent,
        remoteContact,
        ...personalInformation
    } = data;

    return {
        personalInformation: {
            ...personalInformation,
            city: "Oslo",
            streetAddress: "N",
            postalCode: "0010",
            personalIdNumber: personalInformation.personalIdNumber.toString(),
            parentPersonalIdNumber: requireParentPersonalIdNumber
                ? personalInformation.parentPersonalIdNumber
                : null,
            mobileNumber: personalInformation.mobileNumber.toString(),
        },
        notes,
        applyForMembership: subscribeToNewsletter,
        subscribeToNewsletter,
        contentId: contentId.toString()
    };
}

export function thirdStepDataDropInParser(
  data: FromThirdStepData,
  contentId: number,
  requireParentPersonalIdNumber: boolean,
  remoteOption?: AppointmentOptionDto,
  code?: number,
  dropInResponse?: CreateBookingResponseDto
) {
  const {
    subscribeToNewsletter,
    notes,
    terms,
    consent,
    remoteContact,
    ...personalInformation
  } = data;

  const request = {
    remoteAppointmentOption: remoteOption,
    personalInformation: {
      ...personalInformation,
      city: "Oslo",
      firstName: "F",
      lastName: "L",
      streetAddress: "N",
      postalCode: "0010",
      personalIdNumber: personalInformation.personalIdNumber.toString(),
      parentPersonalIdNumber: requireParentPersonalIdNumber
        ? personalInformation.parentPersonalIdNumber
        : null,
      mobileNumber: personalInformation.mobileNumber.toString(),
    },
    notes,
    applyForMembership: subscribeToNewsletter,
    subscribeToNewsletter,
  };

  return code
    ? {
        bookingSessionId: dropInResponse?.bookingSessionId,
        booking: {
          ...dropInResponse?.booking,
          ...request,
          waitlistContentId: contentId.toString(),
        },
        confirmationCode: code.toString(),
      }
    : { ...request, contentId: contentId.toString() };
}
